import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toastError, errorText } from '../../../shared/toastHelper';

export const budgetTemplateSeed = (category, v3_budget_id, position) => {
  const base = { v3_budget_id, category };
  switch (category) {
    case 'TICKET':
      return { ...base, name: `Ticket #${position}`, operator: '*', left_hand: 0, right_hand: 0, available: 0, comps: 0, kills: 0 };
    case 'INCOME':
      return { ...base, name: `Other Income #${position}`, operator: '*', calc_type: 'fixed', left_hand: 0, right_hand: 1, right_hand_forecast: 1 };
    case 'EXPENSES':
      return { ...base, name: `Expense #${position}`, operator: '*', calc_type: 'fixed', left_hand: 0, right_hand: 1, right_hand_forecast: 1 };
    case 'ADDITION':
      return { ...base, name: `Addition #${position}`, operator: '*', calc_type: 'fixed', left_hand: 0, right_hand: 1, right_hand_forecast: 1, performer_id: null };
    case 'BEFORE_ADJUSTED_DEDUCTIONS':
    case 'DEDUCTIONS':
    default:
      return { ...base, name: `Deduction #${position}`, operator: '*', calc_type: 'per_tickets_sold', left_hand: 0, right_hand: 0 };
  }
};

const useV3BudgetTemplate = ({ team, templateId, csrfToken }) => {
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadRef = useRef(0);
  const headers = { 'X-CSRF-TOKEN': csrfToken };
  const base = `/teams/${team.id}/budget_templates/v3/${templateId}`;

  // Clear budget at load start (so a reopened same template re-hydrates from the
  // fresh response instead of showing the cached one) and stamp each load with an
  // id — only the latest load may update state, so an out-of-order response from a
  // superseded template can never overwrite the current one.
  const load = useCallback(() => {
    const loadId = loadRef.current + 1;
    loadRef.current = loadId;
    setBudget(null);
    setLoading(true);
    return axios.get(base, { headers })
      .then((r) => { if (loadId === loadRef.current) setBudget(r.data); return r.data; })
      .catch(() => { if (loadId === loadRef.current) toastError("Couldn't load budget template."); })
      .finally(() => { if (loadId === loadRef.current) setLoading(false); });
  }, [base, csrfToken]);

  // Line-item CRUD are pure network calls: EditBudgetTemplateUI's localBudget is
  // the display source of truth (it reads the hook's budget only at initial load),
  // so mutating budget's line-item list here would be dead work. createLineItem
  // still returns the created row — the container appends it to localBudget.
  const createLineItem = useCallback((params) => (
    axios.post(`${base}/line_items`, { v3_budget_line_item: params }, { headers })
      .then((r) => r.data)
      .catch((e) => toastError(errorText(e, 'Failed to add line item.')))
  ), [base, csrfToken]);

  const updateLineItem = useCallback((id, params) => (
    axios.patch(`${base}/line_items/${id}`, { v3_budget_line_item: params }, { headers })
      .then((r) => r.data)
      .catch((e) => toastError(errorText(e, 'Failed to save line item.')))
  ), [base, csrfToken]);

  const deleteLineItem = useCallback((id) => (
    axios.delete(`${base}/line_items/${id}`, { headers })
      .catch((e) => toastError(errorText(e, 'Failed to delete line item.')))
  ), [base, csrfToken]);

  const reorderLineItem = useCallback((id, position) => (
    axios.patch(`${base}/line_items/${id}`, { v3_budget_line_item: { position } }, { headers })
      .catch((e) => toastError(errorText(e, 'Failed to reorder.')))
  ), [base, csrfToken]);

  const updateBudget = useCallback((params) => (
    axios.patch(base, { v3_budget_template: params }, { headers })
      .then((r) => { setBudget((b) => ({ ...b, ...r.data })); return r.data; })
      .catch(() => toastError('Failed to save.'))
  ), [base, csrfToken]);

  const seedFor = useCallback((category, position) => budgetTemplateSeed(category, budget?.id, position), [budget]);

  return { budget, loading, load, createLineItem, updateLineItem, deleteLineItem, reorderLineItem, updateBudget, seedFor };
};

export default useV3BudgetTemplate;
